import React, { useEffect, useRef, useState } from 'react';
import { MapPin, AlertCircle } from 'lucide-react';

const AddressInput = ({ value, onChange, placeholder, error }) => {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    // Check if Google Maps is already loaded
    if (window.google && window.google.maps && window.google.maps.places) {
      initializeAutocomplete();
      setIsLoaded(true);
      return;
    }

    // Check if script is already loading
    if (document.querySelector('script[src*="maps.googleapis.com"]')) {
      const checkLoaded = setInterval(() => {
        if (window.google && window.google.maps && window.google.maps.places) {
          clearInterval(checkLoaded);
          initializeAutocomplete();
          setIsLoaded(true);
        }
      }, 100);
      return () => clearInterval(checkLoaded);
    }

    // Load Google Maps script
    const script = document.createElement('script');
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'YOUR_GOOGLE_MAPS_API_KEY_HERE';
    
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    
    script.onload = () => {
      if (window.google && window.google.maps && window.google.maps.places) {
        initializeAutocomplete();
        setIsLoaded(true);
      } else {
        setLoadError('Failed to initialize Google Maps');
      }
    };
    
    script.onerror = () => {
      setLoadError('Failed to load Google Maps. Please check your API key.');
    };
    
    document.head.appendChild(script);

    return () => {
      if (autocompleteRef.current) {
        window.google?.maps?.event?.clearInstanceListeners(autocompleteRef.current);
      }
    };
  }, []);

  const initializeAutocomplete = () => {
    if (!inputRef.current || !window.google) return;

    try {
      // Create autocomplete instance with Chicago area bias
      autocompleteRef.current = new window.google.maps.places.Autocomplete(
        inputRef.current,
        {
          types: ['establishment'],
          componentRestrictions: { country: 'US' },
          bounds: new window.google.maps.LatLngBounds(
            new window.google.maps.LatLng(41.6445, -88.0833), // Southwest corner of Chicago area
            new window.google.maps.LatLng(42.0677, -87.5265)  // Northeast corner of Chicago area
          ),
          strictBounds: false, // Allow selection outside bounds but bias towards Chicago
          fields: ['address_components', 'formatted_address', 'geometry', 'place_id', 'name']
        }
      );

      // Listen for place selection
      autocompleteRef.current.addListener('place_changed', handlePlaceSelect);
    } catch (error) {
      console.error('Error initializing Google Places Autocomplete:', error);
      setLoadError('Failed to initialize address autocomplete');
    }
  };

  const handlePlaceSelect = () => {
    if (!autocompleteRef.current) return;

    const place = autocompleteRef.current.getPlace();
    
    if (!place || !place.geometry) {
      console.log('No details available for selected place');
      return;
    }

    // Extract address components
    const addressComponents = place.address_components || [];
    const getComponent = (type) => {
      const component = addressComponents.find(comp => comp.types.includes(type));
      return component ? component.long_name : '';
    };

    // Check if the address is in Illinois (or nearby states for surrounding areas)
    const state = getComponent('administrative_area_level_1');
    const allowedStates = ['Illinois', 'IL', 'Wisconsin', 'WI', 'Indiana', 'IN'];
    
    if (!allowedStates.some(allowedState => 
      state.toLowerCase().includes(allowedState.toLowerCase())
    )) {
      // Show warning but still allow the selection
      console.warn('Address may be outside our primary service area');
    }

    // Build complete address string with business name
    const businessName = place.name || '';
    const streetNumber = getComponent('street_number');
    const streetName = getComponent('route');
    const city = getComponent('locality');
    const stateCode = getComponent('administrative_area_level_1');
    const zipCode = getComponent('postal_code');
    
    let completeAddress = '';
    if (businessName) {
      completeAddress += businessName;
    }
    if (streetNumber && streetName) {
      completeAddress += completeAddress ? `, ${streetNumber} ${streetName}` : `${streetNumber} ${streetName}`;
    }
    if (city) {
      completeAddress += completeAddress ? `, ${city}` : city;
    }
    if (stateCode) {
      completeAddress += completeAddress ? `, ${stateCode}` : stateCode;
    }
    if (zipCode) {
      completeAddress += completeAddress ? ` ${zipCode}` : zipCode;
    }
    console.log(completeAddress, place);
    const addressData = {
      formattedAddress: completeAddress || place.formatted_address,
      coordinates: {
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng()
      },
      components: {
        businessName: businessName,
        streetNumber: getComponent('street_number'),
        streetName: getComponent('route'),
        city: getComponent('locality'),
        state: getComponent('administrative_area_level_1'),
        zipCode: getComponent('postal_code'),
        county: getComponent('administrative_area_level_2')
      },
      placeId: place.place_id
    };

    // Call onChange with complete address data
    onChange(addressData);
  };

  const handleInputChange = (e) => {
    const inputValue = e.target.value;
    
    // If user is typing manually, call onChange with simple string
    onChange({
      formattedAddress: inputValue,
      coordinates: null,
      components: {},
      placeId: null
    });
  };

  if (loadError) {
    return (
      <div>
        <label className="block body-medium font-medium text-gray-900 mb-2">
          Property Address
        </label>
        <div className="relative">
          <MapPin className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
          <input
            type="text"
            value={typeof value === 'string' ? value : value?.formattedAddress || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder || "Enter property address or business name"}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
          />
          {error && (
            <div className="absolute right-3 top-3 h-5 w-5">
              <AlertCircle className="h-5 w-5 text-red-500" />
            </div>
          )}
        </div>
        <div className="mt-1 flex items-center gap-1 text-sm text-yellow-600">
          <AlertCircle className="h-4 w-4" />
          <span>Address autocomplete unavailable. {loadError}</span>
        </div>
        {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
      </div>
    );
  }

  return (
    <div>
      <label className="block body-medium font-medium text-gray-900 mb-2">
        Property Address
      </label>
      <div className="relative">
        <MapPin className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
        <input
          ref={inputRef}
          type="text"
          value={typeof value === 'string' ? value : value?.formattedAddress || ''}
          onChange={handleInputChange}
          placeholder={placeholder || "Enter property address or business name"}
          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
        />
        {error && (
          <div className="absolute right-3 top-3 h-5 w-5">
            <AlertCircle className="h-5 w-5 text-red-500" />
          </div>
        )}
      </div>
      {!isLoaded && !loadError && (
        <p className="mt-1 text-sm text-gray-500">Loading address autocomplete...</p>
      )}
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
};

export default AddressInput;